import * as React from "react";

import {
  OneTimePasswordUserExperience_Step,
  useOneTimePasswordUserExperience,
} from "../one-time-password-provider";

import {
  JoinDrawerUserExperienceProvider,
  JoinDrawerUserExperience_Step,
  useJoinDrawerUserExperience,
} from "./join-drawer-user-experience-provider";

interface JoinDrawerConsumerInterface {
  presentingUserExperienceStep: `${
    | JoinDrawerUserExperience_Step
    | OneTimePasswordUserExperience_Step}`;
  showBackButton?: boolean;
  handleBack: () => void;
  handleClose: () => void;
}
const defaultValue = {} as unknown as JoinDrawerConsumerInterface;
const JoinDrawerContext = React.createContext(defaultValue);

interface JoinDrawerProviderProps {
  onlyUsedForOneTimePassword?: boolean;
}
const ActualJoinDrawerProvider: React.FunctionComponent<
  JoinDrawerProviderProps
> = (props) => {
  const {
    presentingUserExperienceStep: joinDrawer_presentingUserExperienceStep,
    handleClose: joinDrawer_handleClose,
  } = useJoinDrawerUserExperience();

  const {
    presentingUserExperienceStep: oneTimePassword_presentingUserExperienceStep,
    showBackButton: oneTimePassword_showBackButton,
    handleBack: oneTimePassword_handleBack,
    handleClose: oneTimePassword_handleClose,
  } = useOneTimePasswordUserExperience();

  const presentingUserExperienceStep = React.useMemo(() => {
    if (
      oneTimePassword_presentingUserExperienceStep !==
      OneTimePasswordUserExperience_Step.NOT_PRESENTING
    ) {
      return oneTimePassword_presentingUserExperienceStep;
    }
    return joinDrawer_presentingUserExperienceStep;
  }, [
    joinDrawer_presentingUserExperienceStep,
    oneTimePassword_presentingUserExperienceStep,
  ]);

  const showBackButton = React.useMemo(() => {
    if (props.onlyUsedForOneTimePassword) {
      return (
        oneTimePassword_presentingUserExperienceStep ===
        OneTimePasswordUserExperience_Step.SECOND_STEP_VERIFY_CODE
      );
    } else {
      return (
        oneTimePassword_presentingUserExperienceStep !==
        OneTimePasswordUserExperience_Step.NOT_PRESENTING
      );
    }
  }, [
    props.onlyUsedForOneTimePassword,
    oneTimePassword_presentingUserExperienceStep,
  ]);

  const handleBack = React.useCallback(() => {
    const isTheDrawerCurrentlyInOneTimePasswordMode =
      oneTimePassword_presentingUserExperienceStep !==
      OneTimePasswordUserExperience_Step.NOT_PRESENTING;
    if (isTheDrawerCurrentlyInOneTimePasswordMode) {
      if (oneTimePassword_showBackButton) {
        oneTimePassword_handleBack();
      } else {
        oneTimePassword_handleClose();
      }
    }
  }, [
    oneTimePassword_presentingUserExperienceStep,
    oneTimePassword_showBackButton,
    oneTimePassword_handleBack,
    oneTimePassword_handleClose,
  ]);

  const handleClose = React.useCallback(() => {
    oneTimePassword_handleClose();
    joinDrawer_handleClose();
  }, [oneTimePassword_handleClose, joinDrawer_handleClose]);

  const value: JoinDrawerConsumerInterface = React.useMemo(
    () => ({
      presentingUserExperienceStep,
      showBackButton,
      handleBack,
      handleClose,
    }),
    [presentingUserExperienceStep, showBackButton, handleBack, handleClose]
  );
  return (
    <JoinDrawerContext.Provider value={value}>
      {props.children || []}
    </JoinDrawerContext.Provider>
  );
};

export const useJoinDrawer = () => {
  const value = React.useContext(JoinDrawerContext);

  if (value === defaultValue) {
    throw new Error("useJoinDrawer must be used within a JoinDrawerProvider");
  }

  return value;
};

export const JoinDrawerProvider: React.FunctionComponent<
  JoinDrawerProviderProps
> = (props) => {
  return (
    <React.Fragment>
      <JoinDrawerUserExperienceProvider>
        <ActualJoinDrawerProvider
          onlyUsedForOneTimePassword={props.onlyUsedForOneTimePassword}
        >
          {props.children || []}
        </ActualJoinDrawerProvider>
      </JoinDrawerUserExperienceProvider>
    </React.Fragment>
  );
};
